import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/base/PageLayout';
import NotFound from '../NotFound';
import { FORM_ENDPOINT } from '../../config/form';
import enContent from '../../data/en-content.json';

type Item = { title: string; text: string; href?: string };
type Section = { id: string; heading: string; body: string[]; items?: Item[] };
type Entry = { route: string; meta: { title: string; description: string; keywords: string[] }; sections: Section[] };
const entries = enContent as Entry[];
const entryMap = new Map(entries.map((entry) => [entry.route, entry]));
const sitemapSections = [
  { heading: 'Main', items: [{ title: 'Home', href: '/en' }, { title: 'About Butler Bell', href: '/en/about' }] },
  { heading: 'Services', items: [
    { title: 'All Services', href: '/en/services' },
    { title: 'Corporate Training', href: '/en/services/corporate-training' },
    { title: 'Private Butler', href: '/en/services/private-butler' },
    { title: 'Manner Courses', href: '/en/services/manner-course' },
    { title: 'Consulting', href: '/en/services/consulting' },
  ] },
  { heading: 'Butler Wisdom', items: [
    { title: 'Butler Wisdom', href: '/en/wisdom' },
    { title: 'Hospitality', href: '/en/wisdom/hospitality' },
    { title: 'Etiquette', href: '/en/wisdom/etiquette' },
    { title: 'Butler Skills', href: '/en/wisdom/butler-skills' },
    { title: 'Elegance', href: '/en/wisdom/elegance' },
    { title: 'All Articles', href: '/en/wisdom/articles' },
  ] },
  { heading: 'Articles', items: [
    { title: 'VIP Service', href: '/en/wisdom/articles/vip-service' },
    { title: 'Communication', href: '/en/wisdom/articles/communication' },
    { title: 'Butler Mindset', href: '/en/wisdom/articles/mindset' },
    { title: 'Table Manners', href: '/en/wisdom/articles/table-manners' },
    { title: 'Dress Code', href: '/en/wisdom/articles/dress-code' },
    { title: 'Social Etiquette', href: '/en/wisdom/articles/social-etiquette' },
    { title: 'Daily Routine', href: '/en/wisdom/articles/daily-routine' },
    { title: 'Observation', href: '/en/wisdom/articles/observation' },
    { title: 'Time Management', href: '/en/wisdom/articles/time-management' },
    { title: 'Posture', href: '/en/wisdom/articles/posture' },
    { title: 'Speech', href: '/en/wisdom/articles/speech' },
    { title: 'Organisation', href: '/en/wisdom/articles/organization' },
  ] },
  { heading: 'Information', items: [
    { title: 'Media', href: '/en/media' },
    { title: 'Contact', href: '/en/contact' },
    { title: 'Privacy Policy', href: '/en/privacy' },
    { title: 'Legal Notice', href: '/en/legal' },
    { title: 'Sitemap', href: '/en/sitemap' },
  ] },
];

const sitemapTitleByHref = new Map(
  sitemapSections.flatMap((section) => section.items.map((item) => [item.href, item.title] as const))
);

function buildBreadcrumbItems(route: string): { name: string; href?: string }[] {
  const items: { name: string; href?: string }[] = [{ name: 'Home', href: '/en' }];
  const segments = route.split('/').filter(Boolean);
  let cumulative = '';
  segments.forEach((segment, index) => {
    cumulative += `/${segment}`;
    const href = `/en${cumulative}`;
    const name = sitemapTitleByHref.get(href) ?? segment.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    const isLast = index === segments.length - 1;
    items.push(isLast ? { name } : { name, href });
  });
  return items;
}

const heroImages: Record<string, string> = {
  services: '/images/generated/seq-services-hero-elegant-b80dc4.jpg',
  wisdom: '/images/generated/seq-wisdom-hero-library-4be2bb.jpg',
  media: '/images/generated/seq-media-hero-studio-ea820a.jpg',
  contact: '/images/generated/seq-contact-hero-desk-e07c81.jpg',
  about: '/images/generated/seq-about-hero-hands-106b9d.jpg',
};

function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', service: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [hp, setHp] = useState('');
  const [shownAt] = useState(() => Date.now());
  const [submissionId] = useState(() => crypto.randomUUID());
  const change = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!FORM_ENDPOINT) { setStatus('error'); return; }
    setStatus('sending');
    try {
      const response = await fetch(FORM_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify({ form: 'contact', lang: 'en', ...form, _hp: hp, page: window.location.href, userAgent: navigator.userAgent, formShownAt: shownAt, submissionId }) });
      const result = await response.json() as { status?: string };
      if (!response.ok || result.status !== 'ok') throw new Error('Submission failed');
      setForm({ name: '', email: '', company: '', phone: '', service: '', message: '' });
      setStatus('success');
    } catch { setStatus('error'); }
  };
  const fieldClass = 'w-full rounded-lg border border-gray-300 px-4 py-3 text-base focus:border-[#917c50] focus:ring-2 focus:ring-[#917c50]/30';
  return (
    <section className="bg-[#faf8f5] py-16 md:py-20" id="contact-form">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8 md:px-8">
        <h2 className="mb-8 text-center text-3xl font-medium text-[#1a1a1a]" style={{ fontFamily: '"Noto Serif", "Noto Serif JP", serif' }}>Contact Form</h2>
        <form onSubmit={submit} className="space-y-6 rounded-2xl bg-white p-6 shadow-lg md:p-10">
          <div className="hidden" aria-hidden="true"><label>Leave blank<input value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" /></label></div>
          <div className="grid gap-6 md:grid-cols-2">
            <label className="text-base font-medium">Name *<input className={`${fieldClass} mt-2`} name="name" value={form.name} onChange={change} required /></label>
            <label className="text-base font-medium">Email *<input className={`${fieldClass} mt-2`} type="email" name="email" value={form.email} onChange={change} required /></label>
            <label className="text-base font-medium">Company or organisation<input className={`${fieldClass} mt-2`} name="company" value={form.company} onChange={change} /></label>
            <label className="text-base font-medium">Telephone<input className={`${fieldClass} mt-2`} type="tel" name="phone" value={form.phone} onChange={change} /></label>
          </div>
          <label className="block text-base font-medium">Service
            <select className={`${fieldClass} mt-2`} name="service" value={form.service} onChange={change}><option value="">Please select</option><option>Corporate Training &amp; Seminars</option><option>Private Butler Service</option><option>Manner Courses</option><option>Consulting</option><option>Other</option></select>
          </label>
          <label className="block text-base font-medium">Message *<textarea className={`${fieldClass} mt-2 min-h-40`} name="message" value={form.message} onChange={change} required maxLength={500} /></label>
          {status === 'success' && <p className="rounded-lg bg-green-50 p-4 text-green-800">Thank you. Your enquiry has been sent.</p>}
          {status === 'error' && <p className="rounded-lg bg-red-50 p-4 text-red-800">We could not send your enquiry. Please email info@butlerbell.jp.</p>}
          <button className="btn-gold w-full rounded-lg bg-[#A26A1D] px-8 py-4 text-base font-medium text-white transition-colors hover:bg-[#8A5A18] disabled:opacity-60" disabled={status === 'sending'}>{status === 'sending' ? 'Sending…' : 'Send Enquiry'}</button>
        </form>
      </div>
    </section>
  );
}

export default function EnglishPage({ route }: { route: string }) {
  const entry = entryMap.get(route);
  useEffect(() => {
    if (!entry) return;
    document.title = entry.meta.title;
    document.documentElement.lang = 'en';
    const description = document.querySelector('meta[name="description"]');
    description?.setAttribute('content', entry.meta.description);
    return () => { document.documentElement.lang = 'ja'; };
  }, [entry]);
  if (!entry) return <NotFound />;
  const sections = Array.isArray(entry.sections) ? entry.sections : [];
  const hero = sections[0];
  if (!hero) return <NotFound />;
  const pageTitle = entry.meta.title.split('|')[0].trim();
  const isArticle = route.startsWith('/wisdom/articles/') && route !== '/wisdom/articles';
  const imageKey = Object.keys(heroImages).find((key) => route.includes(key)) || (isArticle ? 'wisdom' : 'about');
  return (
    <PageLayout
      {...(route === '/' ? { showBreadcrumb: false } : { breadcrumbItems: buildBreadcrumbItems(route) })}
      hero={
        <header className="relative flex min-h-[430px] items-center justify-center overflow-hidden bg-[#171717] px-5 sm:px-6 lg:px-8 py-24 text-center text-white">
          <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url('${heroImages[imageKey]}')` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/45 to-black/75" />
          <div className="relative mx-auto max-w-6xl">
            <p className="mb-5 text-sm uppercase tracking-[0.3em] text-amber-400">Butler Bell</p>
            <h1 className="text-4xl font-medium leading-tight md:text-6xl" style={{ fontFamily: '"Noto Serif", "Noto Serif JP", serif' }}>{hero.heading === 'Introduction' ? pageTitle : hero.heading}</h1>
            {(Array.isArray(hero.body) ? hero.body : []).map((paragraph, index) => <p key={`${hero.id}-${index}-${paragraph}`} className="mx-auto mt-6 max-w-6xl text-lg leading-8 text-gray-200">{paragraph}</p>)}
          </div>
        </header>
      }
    >
      <article lang="en" className="font-sans text-[#292929]">
        <div className="bg-white">
          {route === '/sitemap' ? sitemapSections.map((section, sectionIndex) => (
            <section key={section.heading} className={sectionIndex % 2 ? 'bg-[#faf8f5] py-16 md:py-24' : 'bg-white py-16 md:py-24'}>
              <div className="mx-auto max-w-6xl px-5 md:px-8">
                <div className="mb-10 flex items-center gap-4"><span className="h-px w-12 bg-[#b69654]" /><h2 className="text-3xl font-medium leading-tight text-[#1a1a1a] md:text-4xl" style={{ fontFamily: '"Noto Serif", "Noto Serif JP", serif' }}>{section.heading}</h2></div>
                <ul className="grid gap-4 md:grid-cols-2">{section.items.map((item) => <li key={item.href}><Link to={item.href} className="block border border-[#d8c49b] bg-white p-5 text-lg font-medium text-[#6f5b31] shadow-sm transition-colors hover:text-[#A26A1D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#A26A1D]">{item.title}</Link></li>)}</ul>
              </div>
            </section>
          )) : sections.slice(1).map((section, index) => (
            <section key={`${section.id}-${index}`} id={section.id} className={index % 2 ? 'bg-[#faf8f5] py-16 md:py-24' : 'bg-white py-16 md:py-24'}>
              <div className="mx-auto max-w-6xl px-5 md:px-8">
                <div className="mb-10 flex items-center gap-4"><span className="h-px w-12 bg-[#b69654]" /><h2 className="text-3xl font-medium leading-tight text-[#1a1a1a] md:text-4xl" style={{ fontFamily: '"Noto Serif", "Noto Serif JP", serif' }}>{section.heading}</h2></div>
                <div className="max-w-6xl space-y-5 text-base leading-8 text-gray-700 md:text-lg">{(Array.isArray(section.body) ? section.body : []).map((paragraph, paragraphIndex) => <p key={`${section.id}-${paragraphIndex}-${paragraph}`}>{paragraph}</p>)}</div>
                {Array.isArray(section.items) && <div className="mt-10 grid gap-6 md:grid-cols-2">{section.items.map((item, itemIndex) => <div key={`${section.id}-${itemIndex}-${item.title}`} className="border border-[#d8c49b] bg-white p-7 shadow-sm"><h3 className="mb-3 text-xl font-medium text-[#6f5b31]" style={{ fontFamily: '"Noto Serif", "Noto Serif JP", serif' }}>{item.href ? <Link to={item.href} className="transition-colors hover:text-[#A26A1D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#A26A1D]">{item.title}</Link> : item.title}</h3><p className="text-base leading-7 text-gray-700">{item.text}</p></div>)}</div>}
              </div>
            </section>
          ))}
        </div>
        {route === '/contact' && <ContactForm />}
      </article>
    </PageLayout>
  );
}

export { entries as englishEntries };
